import {
  Flex,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  useColorModeValue,
  Input as ChakraInput,
  InputProps,
} from '@chakra-ui/react';
import { Control, Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

interface InputNumberProps extends InputProps {
  label?: string;
  color?: string;
  name?: string;
  value?: any;
  step?: number;
  min?: number;
  max?: number;
  control?: Control<any>;
  error?: any;
  maskType?: 'money' | 'default';
  isDisabled?: boolean;
  isReadOnly?: boolean;
  onChange?: (value: any) => void;
}

export default function InputNumber({
  isDisabled,
  isReadOnly,
  error,
  label,
  color,
  maskType = 'default',
  name,
  control,
  min,
  max,
  value,
  onChange,
  ...rest
}: InputNumberProps) {
  // const textColor = useColorModeValue('black', 'white');
  const errorColor = useColorModeValue('rgb(155, 0, 0)', 'rgb(250, 100, 100)');

  const formatBR = (n: number | null | undefined) =>
    new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(Number.isFinite(n as number) ? (n as number) : 0);

  const toNumber = (val: any): number => {
    if (val === null || val === undefined || val === '') return 0;
    const num = Number(val);
    return Number.isNaN(num) ? 0 : num;
  };

  const parseBR = (s: string) => {
    if (!s) return 0;
    const normalized = s.replace(/\./g, '').replace(',', '.');
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
  };

  if (control && name) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Flex
            flex={1}
            direction="column"
            width="100%"
            borderColor={errorColor}
            {...rest}
          >
            <Text
              fontSize="16px"
              justifyContent="center"
              fontWeight="medium"
              color={color}
            >
              {label}
            </Text>
            {maskType === 'money' ? (
              <NumericFormat
                isDisabled={isDisabled}
                {...(isDisabled ? { isDisabled: true } : {})}
                value={field.value ?? 0}
                decimalSeparator=","
                thousandSeparator="."
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                customInput={ChakraInput}
                placeholder="R$ 0,00"
                isAllowed={values => {
                  const v = values.floatValue ?? 0;

                  if (min !== undefined && v < min) return false;
                  if (max !== undefined && v > max) return false;
                  return true;
                }}
                onValueChange={vals => {
                  const v = vals.floatValue ?? 0;
                  field.onChange(v);
                  onChange?.(v);
                }}
              />
            ) : (
              <>
                <NumberInput
                  value={field.value ?? 0}
                  onChange={(valueStr, valueNum) => {
                    const safe = Number.isFinite(valueNum)
                      ? valueNum
                      : parseBR(valueStr);
                    field.onChange(safe);
                    onChange?.(safe);
                  }}
                  step={0.0001}
                  precision={3}
                  format={formatBR}
                  // parse={parseBR}
                  min={0}
                  max={999999}
                  color="red"
                >
                  <NumberInputField
                    placeholder="0,0000"
                    pattern="[0-9\.,]*"
                    inputMode="decimal"
                    onBlur={e => {
                      const parcedValue = parseBR(e.target.value);
                      e.target.value = formatBR(parcedValue);
                    }}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </>
            )}

            {error && (
              <Text color={errorColor} fontSize={13}>
                {error.message}
              </Text>
            )}
          </Flex>
        )}
      />
    );
  }

  return (
    <Flex
      direction="column"
      flex={1}
      width="100%"
      borderColor={error ? errorColor : 'inherit'}
      {...rest}
    >
      <Text
        fontSize="16px"
        justifyContent="center"
        fontWeight="medium"
        color={color}
      >
        {label}
      </Text>

      {maskType === 'money' ? (
        <NumericFormat
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          // {...(isDisabled ? { isDisabled: true } : {})}
          value={value ?? 0}
          decimalSeparator=","
          thousandSeparator="."
          decimalScale={2}
          fixedDecimalScale
          allowNegative={false}
          customInput={ChakraInput}
          placeholder="R$ 0,00"
          color={color}
          isAllowed={values => {
            const v = values.floatValue ?? 0;

            if (min !== undefined && v < min) return false;
            if (max !== undefined && v > max) return false;
            return true;
          }}
          onValueChange={value => {
            const v = value.floatValue ?? 0;
            onChange?.(v);
          }}
          {...rest}
        />
      ) : (
        <>
          <NumberInput
            borderColor={error ? errorColor : 'inherit'}
            disabled={isDisabled}
            value={value ?? 0}
            min={min}
            max={max}
            color="red"
            // step={0.01}
            // precision={2}
            onChange={(_, valNumber) => {
              const safe = toNumber(valNumber);
              onChange?.(safe);
            }}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </>
      )}

      {error && (
        <Text color={errorColor} fontSize={13}>
          {error.message}
        </Text>
      )}
    </Flex>
  );
}
